// Custom error class for authentication failures
class AuthenticationError extends Error {
  constructor(message, status = 401) {
    super(message)
    this.name = 'AuthenticationError'
    this.status = status
  }
}

// Custom error class for timeouts and gateway errors (server likely still processing)
class TimeoutError extends Error {
  constructor(message) {
    super(message)
    this.name = 'TimeoutError'
  }
}

// We need to get the token from outside this service since useAuth needs component context
import { buildKnowledgeCardPayload } from '../utils/knowledgeCardPayload.js'
// Embed-safe: utils/ constant, not ../i18n (see supportedLocales.js + embed guard).
import { SUPPORTED_LOCALES } from '../utils/supportedLocales.js'
import { peekSignupReferrer, SIGNUP_REFERRER_PARAM } from '../utils/signupReferrer.js'
import { httpStatusFromError, IDEMPOTENT_METHODS, isTransportFailure, noteTransportOutcome, pause, shouldRetryRequest, transportRetryDelayMs } from '../utils/requestRetry.js'
import { isAlreadyDeleted, shouldCountDeletion } from '../utils/deleteOutcome.js'
import { serverLinkId } from '../utils/pendingBookmark.js'
import { apiStatusLabel, isSuspendedPageAbort } from '../utils/apiStatusLabel.js'

// Resolved app UI locale for the Accept-Language header. Read from localStorage
// (useLocale.applyLocale persists it before mount) instead of importing the i18n
// module: keeps api.js dependency-free for the embed bundle and cycle-proof
// (SUPPORTED_LOCALES comes from utils/, not ../i18n — the embed guard forbids
// the latter). Only the exact bare supported tags are sent — the backend stores
// the language at user creation from this header, and its whitelist ignores
// anything else (browser-default Accept-Language values can never stamp a user).
function currentUiLocale() {
  try {
    const stored = localStorage.getItem('linkflare_locale')
    if (SUPPORTED_LOCALES.includes(stored)) {
      return stored
    }
  } catch (e) {
    // localStorage unavailable (private browsing) — default to English
  }
  return 'en'
}

// `?preview=true` makes an import endpoint parse and count without starting:
// the import then waits for startImportTask (see useImportStart).
function importQuery(options = {}) {
  return options.preview ? '?preview=true' : ''
}

// Appends the captured acquisition referrer as a query param. It is a param and
// NOT a custom header on purpose: a custom header needs an explicit entry in the
// backend's Access-Control-Allow-Headers, and while that entry was missing every
// authenticated call from an ad-click session failed the CORS preflight outright
// ("Failed to fetch" / "Load failed" — the request never left the browser). A
// query param is never part of the preflight contract.
//
// Only sent alongside a token: the backend reads it inside authorize(), on
// first-time user creation only, and an unauthenticated request never gets
// there. It rides on EVERY authenticated request by design — the first three
// calls after sign-up race and any of them may be the one that creates the user,
// so this can't be narrowed to a single endpoint.
function withSignupReferrer(url, token) {
  if (!token) return url

  const referrer = peekSignupReferrer()
  if (!referrer) return url

  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}${SIGNUP_REFERRER_PARAM}=${encodeURIComponent(referrer)}`
}

// Error reporter injected by initSentry(). api.js must NOT import @sentry/vue
// itself: it sits in the embed-bundle closure (tests/embedNoI18n.test.js) and
// BOTH entry points (main.js, embed-main.js) defer Sentry to an idle-time
// dynamic import, so a static import would bloat both bundles.
//
// This exists because makeAuthenticatedRequest used to only console.error: a
// seven-week total API outage for ad traffic (CORS preflight rejection, see
// withSignupReferrer above) produced ZERO Sentry issues and surfaced only from a
// session replay.
let apiErrorReporter = null

// Failures that happened BEFORE the reporter was injected. Both entry points
// register on requestIdleCallback / a 2s timeout, while the first data load
// fires during mount — so without this buffer the initial-load burst, i.e.
// exactly how a total outage presents, is the one thing that never gets
// reported. Bounded by the same cap as live reporting.
let pendingApiFailures = []

// Hard cap + per-signature dedupe. A broken session fires every endpoint it has;
// unbounded capture would drown the project and burn quota to say one thing.
// A handful of events is all it takes to see an outage.
const MAX_REPORTED_API_FAILURES = 5
// Expected (user-correctable) rejections get their OWN budget. They are reported
// so their rate stays visible — `expectedErrorStatuses` used to drop them, which
// is how ~11 recipe failures a day went unseen until a user wrote in — but they
// must never spend the 5 slots that exist to make a real outage visible.
const MAX_REPORTED_EXPECTED_FAILURES = 3
// The cap must not blind a long-lived tab forever: an SPA left open all day
// would otherwise go permanently silent after its first five failures. Decay the
// budget instead of holding it for the page's lifetime.
const REPORT_WINDOW_MS = 10 * 60 * 1000

let reportedApiFailureSignatures = new Map()
let reportedExpectedFailureSignatures = new Map()

// Opaque ids (/links/123, /collections/abc-def) would make every request its own
// signature: the cap would be spent on five variants of ONE endpoint, and the
// Sentry tag would have unbounded cardinality. Collapse them to :id so a route
// is a route.
function normalizeEndpointPath(endpoint) {
  return String(endpoint)
    .split('?')[0]
    .split('/')
    .map((segment) => {
      if (!segment) return segment
      // Numeric ids, uuids, and anything else that doesn't look like a fixed
      // route word (long, or mixing digits/dashes into a token).
      if (/^\d+$/.test(segment)) return ':id'
      if (/^[0-9a-f]{8}-[0-9a-f-]{4,}$/i.test(segment)) return ':id'
      if (segment.length > 24 || /\d/.test(segment)) return ':id'
      return segment
    })
    .join('/')
}

export function setApiErrorReporter(reporter) {
  // A burst still collecting belongs to the reporter that was live when it failed.
  flushTransportBurst()
  apiErrorReporter = typeof reporter === 'function' ? reporter : null
  reportedApiFailureSignatures = new Map()
  reportedExpectedFailureSignatures = new Map()

  const buffered = pendingApiFailures
  pendingApiFailures = []
  if (!apiErrorReporter) return
  for (const failure of buffered) {
    deliverApiFailure(failure.error, failure.endpoint, failure.method, failure.status, failure.expected)
  }
}

// A page that loses the API fails every request it has in flight at once, and
// the per-route fingerprint below then files each route as its own issue: one
// dropped connection on 2026-09-23 opened or reopened six (LINKFLARE-1W, 1V, 1S,
// 1Q, 17, 13), and 09-18 and 09-22 had the same shape. So a transport failure
// waits (see the window below). Alone, it is then reported on its own route as
// before. Two or more DIFFERENT routes dying in transit together are one event:
// the page lost the API, not N endpoints breaking.
//
// Lost READS only (GET/HEAD, no status, `isTransportFailure`). An HTTP status or
// a TimeoutError is the backend answering — including a 502/503/504, which api.js
// raises as a TimeoutError — and keeps its per-route fingerprint. So does a lost
// WRITE: it may have cost the user a save, and lessons.md is explicit that a lost
// `PUT /links` must never share a group with lost reads again (LINKFLARE-X).
//
// The window is long because a lost connection does not fail everything in one
// second. On 2026-10-10 one session lost every read for ~2 minutes, the failures
// landing 12 to 35 s apart as the user opened settings and changed views. Tempo
// shows none of them arrived while other users got 200s. A 1.5 s window split
// that one outage into new per-route issues (LINKFLARE-23, 24) beside the burst
// issue (LINKFLARE-1Y).
// Any response ends the window early (`noteApiReached`): once a request gets
// through, the page has not lost the API, so a route that keeps failing on its
// own (a blocked CORS preflight, say) is still reported per route. Not for
// failures buffered before the reporter exists: those are replayed into one
// fresh window at init, whatever answered in between.
export const TRANSPORT_BURST_QUIET_MS = 45 * 1000
// Bounds the wait even if failures keep trickling in, so a flapping connection
// cannot hold a report back indefinitely.
export const TRANSPORT_BURST_MAX_MS = 3 * 60 * 1000

let transportBurst = null

function collectTransportFailure(failure) {
  const now = Date.now()
  if (!transportBurst) transportBurst = { failures: [], startedAt: now, timer: null }
  transportBurst.failures.push(failure)
  clearTimeout(transportBurst.timer)
  const wait = Math.min(TRANSPORT_BURST_QUIET_MS, transportBurst.startedAt + TRANSPORT_BURST_MAX_MS - now)
  transportBurst.timer = setTimeout(flushTransportBurst, Math.max(0, wait))
}

/** Reports the collected transport failures now. Also a test seam. */
export function flushTransportBurst() {
  const burst = transportBurst
  transportBurst = null
  if (!burst) return
  clearTimeout(burst.timer)

  const routes = [...new Set(burst.failures.map((f) => `${f.method} ${normalizeEndpointPath(f.endpoint)}`))].sort()
  if (routes.length < 2) {
    for (const f of burst.failures) reportApiFailureNow(f.error, f.endpoint, f.method, f.status, f.expected)
    return
  }

  // Expected only if every member was: one lost foreground read makes it an error.
  const first = burst.failures[0]
  reportApiFailureNow(first.error, first.endpoint, first.method, first.status,
    burst.failures.every((f) => f.expected), routes)
}

/**
 * A response came back (any status, even an intercepting proxy's page), so the
 * page has not lost its connection: close any collecting burst.
 */
function noteApiReached() {
  flushTransportBurst()
}

function deliverApiFailure(error, endpoint, method, status, expected = false) {
  if (status == null && isTransportFailure(error) && IDEMPOTENT_METHODS.has(String(method).toUpperCase())) {
    collectTransportFailure({ error, endpoint, method, status, expected })
    return
  }
  reportApiFailureNow(error, endpoint, method, status, expected)
}

function reportApiFailureNow(error, endpoint, method, status, expected = false, burstRoutes = null) {
  const path = normalizeEndpointPath(endpoint)
  // Status discriminates 404 from 500 from 429 — they all arrive as name
  // "Error", so without it they group and alert as one thing. A burst's signature
  // is its route set: the same page losing the same reads again is a repeat, but
  // a later burst over different routes is new and must still get through.
  const signature = burstRoutes
    ? `burst|${burstRoutes.join(',')}|${error?.name}|${apiStatusLabel(error, status)}`
    : `${method}|${path}|${error?.name}|${apiStatusLabel(error, status)}`
  const now = Date.now()

  const seenSignatures = expected ? reportedExpectedFailureSignatures : reportedApiFailureSignatures
  const budget = expected ? MAX_REPORTED_EXPECTED_FAILURES : MAX_REPORTED_API_FAILURES

  for (const [seen, at] of seenSignatures) {
    if (now - at > REPORT_WINDOW_MS) seenSignatures.delete(seen)
  }

  if (seenSignatures.has(signature)) return
  if (seenSignatures.size >= budget) return
  seenSignatures.set(signature, now)

  try {
    apiErrorReporter(error, { endpoint: path, method, status, expected, ...(burstRoutes && { burstRoutes }) })
  } catch (e) {
    // Reporting must never break the request path.
  }
}

/**
 * Our API answers EVERY error with JSON (`{"status":"failure", "code":…}`), so an
 * HTML error body did not come from it. A captive portal, a corporate proxy or a
 * carrier interception page answered instead and the request never reached the
 * origin — the same "nothing we can do, and nobody is waiting on it" situation a
 * lost request is, but wearing an HTTP status.
 *
 * LINKFLARE-12 / 142796468: a `POST /links/:id/opened` came back as an Apache
 * Tomcat 6.0.37 error page. Server tracing (Tempo, `service.name=api`) shows no
 * span for it at all, and the backend route CANNOT produce that status — it
 * authorizes, records the interaction and returns success unconditionally.
 *
 * Deliberately narrow: only an explicit `text/html` content type or a body that
 * opens as an HTML document counts. A non-JSON content type alone does not — the
 * API returns plain text on some paths, and over-claiming here would downgrade
 * real faults.
 */
export function isForeignErrorBody(contentType, body) {
  const type = typeof contentType === 'string' ? contentType.toLowerCase() : ''
  // An explicit JSON content type is ours, whatever the body turns out to be.
  if (type.includes('application/json')) return false
  if (type.includes('text/html')) return true
  const text = typeof body === 'string' ? body.trimStart() : ''
  return /^<(?:!doctype\s+html|html[\s>])/i.test(text)
}

// True between `pagehide` and a bfcache `pageshow`. A reload or navigation
// aborts every request still in flight, and Chromium rejects each one with the
// same `TypeError: Failed to fetch` a lost request gets, after `pagehide` has
// fired. LINKFLARE-13 and its siblings (1J/1K/1M/1N/1P/1Q) were one session
// reloading ~2s after load: Tempo shows the server answering every one of those
// GETs with 200, and the next page's boot requests arriving a second later.
let pageTearingDown = false
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => {
    // A lost save still waiting for its poll's verdict will not get one now, so
    // it files at full weight. Settled first: the teardown filter would drop it.
    settlePendingLostReports()
    pageTearingDown = true
    // Failures collected before teardown were real; send them before the page goes.
    flushTransportBurst()
  })
  window.addEventListener('pageshow', () => { pageTearingDown = false })
}
// Hidden is the last lifecycle signal a mobile page reliably gets: iOS freezes
// timers once the app is backgrounded and may discard the page without ever
// firing pagehide. Send what is collected rather than leave it on a frozen timer.
if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTransportBurst()
  })
}

// A bookmark save that dies in transit is not yet known to be lost: Bookmarks.vue
// polls the server for the link by URL, and the server usually has it. All ten
// LINKFLARE-11 events on 2026-10-22/23 were saves Tempo shows answered 200 after
// the 15 s analysis budget, while Cloudflare logged the browser closing the
// connection (499) 3 to 7 s in; the poll then found every link and told the user
// it was saved. So a save that opts in (`deferLostReport`) holds its report until
// the poll decides. Found files a warning under its own `landed` label; not found,
// a stopped poll or a page going away first files at full weight, as before.
const pendingLostReports = new Set()

function isBrowserOffline() {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

function deferredLostReport(error, endpoint, method) {
  let settled = false
  const settle = (landed) => {
    if (settled) return
    settled = true
    pendingLostReports.delete(settle)
    if (landed === true) error.landedOnServer = true
    const send = () => reportApiFailure(error, endpoint, method, [], [], landed === true)
    // The page was online when the save failed (checked before holding it), so
    // the offline filter does not apply. Being offline at the verdict, say a
    // poll that gave up in a tunnel, only means Sentry is out of reach: send it
    // once the connection is back rather than let the filter drop it.
    if (isBrowserOffline() && typeof window !== 'undefined') {
      window.addEventListener('online', send, { once: true })
      return
    }
    send()
  }
  pendingLostReports.add(settle)
  return settle
}

function settlePendingLostReports() {
  for (const settle of [...pendingLostReports]) settle(false)
}

function reportApiFailure(error, endpoint, method, expectedErrorStatuses = [], silentErrorStatuses = [], expectedTransportFailure = false, foreignResponse = false) {
  // 401 is routine (token expiry mid-session) and would bury everything else.
  if (error?.name === 'AuthenticationError' || error?.status === 401) return
  // Offline is the user's network, not our bug — the PWA is built to fail here.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  // The page cancelled it by going away; nothing failed and nobody is waiting.
  // Transport only: an HTTP status that arrives during teardown is still real.
  if (pageTearingDown && isTransportFailure(error)) return

  // Parsed off the message rather than the thrown error, which callers pattern
  // match on (see classifyError in the username editor) and must not change.
  const status = httpStatusFromError(error)

  // Two different kinds of "this 4xx is not a bug", and they are NOT the same:
  //
  // silentErrorStatuses — the caller is anonymous public traffic and the volume is
  // not ours to bound. A dead share link 404s once per VISITOR, and crawlers walk
  // stale and guessed public URLs all day; the per-session budget below caps
  // events per session, not the number of sessions. The fact is also not
  // actionable — the content is simply gone. Report nothing. (LINKFLARE-Z)
  //
  // expectedErrorStatuses — an authenticated, in-app, user-correctable rejection
  // where the RATE is the whole signal. These are bounded by real usage, and
  // dropping them is exactly how ~11 rejected recipe extractions a day stayed
  // invisible until a user wrote in. Report, but flagged, so the reporter files
  // at warning under its own fingerprint instead of alerting. (LINKFLARE-Y)
  //
  // "Not an operational alert" must not mean "invisible" — unless nobody could act
  // on it and a stranger controls how often it fires.
  if (silentErrorStatuses.includes(status)) return

  // A transport failure has no status at all, so neither status list can speak
  // for it. `expectedTransportFailure` is the caller saying "nothing reached the
  // server AND nobody is waiting on this" — a best-effort background poll that
  // swallows its own error and re-fires on a timer. Scoped to `status == null`
  // on purpose: it must not be able to redefine what the two status lists mean.
  //
  // `isTransportFailure` is load-bearing, not a belt-and-braces check: a 502/503/
  // 504 is re-thrown as a TimeoutError whose message does not match the status
  // regex above, so a GATEWAY error also arrives here with a null status. That is
  // the backend being down — the opposite of a request lost in transit — and it
  // must keep filing at error level even for a background poll.
  //
  // A response that did not come from our API is flagged the same way, and for
  // the same reason, as a lost background request: nothing reached the origin,
  // and no change we can make will stop an intermediary from answering. It stays
  // REPORTED — the rate is bounded by our own authenticated users, so a rise is
  // worth seeing (lessons.md: downgrade if we could act on the rate, drop only if
  // a stranger controls how often it fires).
  //
  // Scoped to the status branch on purpose. A 502/503/504 arrives here as a
  // TimeoutError with a null status, and that is the backend being down — it must
  // keep paging even if a proxy dressed it in an HTML page.
  //
  // A suspended page is not our backend, and it is not a lost request either —
  // it is the user's phone going to sleep mid-write. iOS freezes timers and
  // stalls `fetch` when the app is backgrounded, so the deadline this error is
  // named after was never actually spent waiting on us (see
  // `utils/apiStatusLabel.js`). It stays REPORTED — our own authenticated
  // users bound the rate, and a rise would say saves are slow enough that people
  // background the app mid-save — but under its own `suspended` label, so it can
  // never share a resolve button with a backend that genuinely hung for 60 s.
  // (LINKFLARE-15 / 143095385.)
  //
  // Set before the status branch on purpose: it is a fact about the ABORT, so no
  // caller opt-in gates it, and nothing with a status can reach it.
  const expected = error?.suspendedPage === true
    ? true
    : status == null
      ? expectedTransportFailure && isTransportFailure(error)
      : foreignResponse || expectedErrorStatuses.includes(status)

  if (!apiErrorReporter) {
    // Budgeted per kind here too, not just at delivery. The buffer exists to catch
    // the initial-load burst — i.e. exactly how a total outage presents — and a
    // single shared cap would let a few validation rejections fill all five slots
    // before Sentry finishes its idle-time init and drop the outage instead.
    const cap = expected ? MAX_REPORTED_EXPECTED_FAILURES : MAX_REPORTED_API_FAILURES
    let queued = 0
    for (const failure of pendingApiFailures) {
      if (!!failure.expected === expected) queued += 1
    }
    if (queued < cap) {
      pendingApiFailures.push({ error, endpoint, method, status, expected })
    }
    return
  }

  deliverApiFailure(error, endpoint, method, status, expected)
}

class ApiService {
  constructor() {
    const nativeBaseURL = typeof window !== 'undefined' && typeof window.__LINKFLARE_API_BASE_URL === 'string'
      ? window.__LINKFLARE_API_BASE_URL
      : null
    this.baseURL = nativeBaseURL || import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/linkflare'
  }

  async makeAuthenticatedRequest(endpoint, options = {}, token = null, { timeout = 0, expectedErrorStatuses = [], silentErrorStatuses = [], expectedTransportFailure = false, deferLostReport = false, retries } = {}) {
    // Retries only ever apply to idempotent reads whose request never reached
    // the server; `shouldRetryRequest` owns that decision. See requestRetry.js.
    let attempt = 0

    for (;;) {
      let controller
      let timeoutId
      let reachedServer = false
      let foreignResponse = false
      let startedAt = Date.now()
      try {
        const headers = {
          'Content-Type': 'application/json',
          // CORS-safelisted header (no preflight change); carried on EVERY request
          // because the first three authenticated calls race and any of them may
          // lazily create the backend user — creation must see the language.
          'Accept-Language': currentUiLocale(),
          ...options.headers
        }

        // Remove Content-Type for FormData to allow browser to set boundary
        if (options.body instanceof FormData) {
          delete headers['Content-Type']
        }

        if (token) {
          headers.Authorization = `Bearer ${token}`
        }

        // Set up abort controller for timeout if specified
        const fetchOptions = { ...options, headers }
        if (timeout > 0) {
          controller = new AbortController()
          fetchOptions.signal = controller.signal
          // Wall clock started alongside the timer, so the two measure the same
          // span. Comparing them in the catch is the only way to tell an abort
          // that waited out our deadline from one whose timer was frozen.
          startedAt = Date.now()
          timeoutId = setTimeout(() => controller.abort(), timeout)
        }

        const response = await fetch(withSignupReferrer(`${this.baseURL}${endpoint}`, token), fetchOptions)
        reachedServer = true
        noteTransportOutcome(true)
        noteApiReached()


        if (!response.ok) {
          const errorText = await response.text()
          foreignResponse = isForeignErrorBody(response.headers?.get('content-type'), errorText)
          console.error('API Error Response:', errorText)
        
          // Handle authentication errors specifically
          if (response.status === 401) {
            throw new AuthenticationError(`Authentication failed: ${errorText}`, 401)
          }
        
          // Treat gateway errors as timeouts — server is likely still processing
          if ([502, 503, 504].includes(response.status)) {
            throw new TimeoutError(`Gateway error: status ${response.status}`)
          }
        
          // Carry the "this body is not our API" verdict on the error itself.
          // The message is load-bearing (callers pattern match on it, and
          // reportApiFailure parses the status out of it) so it must not change
          // — but a caller deciding what a status MEANS needs to know whether
          // our API actually said it. A 404 from an intercepting proxy is not
          // our backend reporting a missing row. (LINKFLARE-12 / LINKFLARE-14)
          const httpError = new Error(`HTTP error! status: ${response.status}, message: ${errorText}`)
          if (foreignResponse) httpError.foreignResponse = true
          throw httpError
        }

        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const jsonResponse = await response.json()
          return jsonResponse
        }
      
        const textResponse = await response.text()
        return textResponse
      } catch (error) {
        // Convert AbortError from timeout into TimeoutError. Reported like any
        // other failure — a request that burned its whole timeout means the
        // backend hung, which is exactly as worth seeing as a 502.
        // An abort that lands long after its own deadline did not measure the
        // backend at all — the page was frozen and the timer fired on resume.
        // The error stays a TimeoutError so every caller's classification is
        // unchanged (the write may still be in flight, so recovery is still the
        // right move); only the REPORTING weight differs. (LINKFLARE-15.)
        let reportable = error
        if (error.name === 'AbortError') {
          reportable = new TimeoutError('Request timed out — the server may still be processing')
          if (isSuspendedPageAbort(Date.now() - startedAt, timeout)) {
            reportable.suspendedPage = true
          }
        }

        // A read that died in transit is worth sending again before we call it a
        // failure: nothing reached the server, so nothing can be duplicated, and
        // the alternative is a broken screen the user can only fix by reloading.
        if (shouldRetryRequest({
          method: options.method || 'GET',
          error,
          attempt,
          reachedServer,
          retries,
          isOnline: typeof navigator === 'undefined' || navigator.onLine !== false
        })) {
          attempt += 1
          await pause(transportRetryDelayMs(attempt))
          continue
        }

        // Giving up: record the outcome so a run of lost requests can suspend
        // retries for everything that follows.
        if (!reachedServer) noteTransportOutcome(false)

        console.error('API request failed:', reportable)
        // Transport only, and only when it would be reported now: an offline or
        // tearing-down page drops it here exactly as before. The caller settles
        // the held report through `settleLostReport` (see deferredLostReport).
        if (deferLostReport && isTransportFailure(reportable) && !isBrowserOffline() && !pageTearingDown) {
          reportable.settleLostReport = deferredLostReport(reportable, endpoint, options.method || 'GET')
        } else {
          reportApiFailure(reportable, endpoint, options.method || 'GET', expectedErrorStatuses, silentErrorStatuses, expectedTransportFailure, foreignResponse)
        }
        throw reportable
      } finally {
        // Per attempt: each one installs its own controller and timer.
        if (timeoutId) clearTimeout(timeoutId)
      }
    }
  }

  // Links API methods
  async getUser(token = null) {
    // Fetch current user info (expects { user: { ... } })
    return this.makeAuthenticatedRequest('/users', { method: 'GET' }, token)
  }

  // Admin (single-email allowlist server-side; UI mirrors it via /admin/whoami)
  async getAdminWhoAmI(token = null) {
    return this.makeAuthenticatedRequest('/admin/whoami', { method: 'GET' }, token)
  }
  async getAdminDripStats(days = 30, token = null) {
    const q = new URLSearchParams({ days: String(days) })
    return this.makeAuthenticatedRequest(`/admin/drip/stats?${q.toString()}`, { method: 'GET' }, token)
  }
  async getAdminChatWeeklySummary(token = null) {
    return this.makeAuthenticatedRequest('/admin/chat/weekly-summary/latest', { method: 'GET' }, token)
  }
  async getAdminOutreachRadar(token = null) {
    return this.makeAuthenticatedRequest('/admin/outreach-radar', { method: 'GET' }, token)
  }
  async refreshAdminOutreachRadar(token = null) {
    return this.makeAuthenticatedRequest('/admin/outreach-radar/refresh', { method: 'POST' }, token)
  }
  async getAdminOutreachRadarTask(taskId, token = null) {
    return this.makeAuthenticatedRequest(`/admin/outreach-radar/tasks/${encodeURIComponent(taskId)}`, { method: 'GET' }, token)
  }
  async updateAdminOutreachOpportunity(id, payload = {}, token = null) {
    return this.makeAuthenticatedRequest(`/admin/outreach-radar/opportunities/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload || {})
    }, token)
  }
  async postAdminOutreachOpportunity(id, token = null) {
    return this.makeAuthenticatedRequest(`/admin/outreach-radar/opportunities/${encodeURIComponent(id)}/post`, {
      method: 'POST'
    }, token)
  }
  async getAdminOutreachKeywords(token = null) {
    return this.makeAuthenticatedRequest('/admin/outreach-radar/keywords', { method: 'GET' }, token)
  }
  async updateAdminOutreachKeywords(payload = {}, token = null) {
    return this.makeAuthenticatedRequest('/admin/outreach-radar/keywords', {
      method: 'PUT',
      body: JSON.stringify(payload || {})
    }, token)
  }
  // Admin: paid-ads ops state (AI-managed campaigns: settings, snapshot, metrics,
  // activity log, recommendations) + first-party signup attribution stats.
  async getAdminAdsOps(token = null) {
    return this.makeAuthenticatedRequest('/admin/ads-ops', { method: 'GET' }, token)
  }
  async updateAdminAdsOpsSettings(payload = {}, token = null) {
    return this.makeAuthenticatedRequest('/admin/ads-ops/settings', {
      method: 'PUT',
      body: JSON.stringify(payload || {})
    }, token)
  }
  async resolveAdminAdsOpsRecommendation(id, status, token = null) {
    return this.makeAuthenticatedRequest(`/admin/ads-ops/recommendations/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    }, token)
  }
  async getAdminPaidSignups(days = 28, token = null) {
    const q = new URLSearchParams({ days: String(days) })
    return this.makeAuthenticatedRequest(`/admin/growth/paid-signups?${q.toString()}`, { method: 'GET' }, token)
  }
  // Admin: LLM knowledge-topic generation + seeding (async; poll the returned task_id)
  async generateKnowledgeTopic(spec, token = null) {
    return this.makeAuthenticatedRequest('/admin/knowledge/topics/generate', {
      method: 'POST', body: JSON.stringify(spec || {})
    }, token)
  }
  async getKnowledgeTopicTask(taskId, token = null) {
    return this.makeAuthenticatedRequest(`/admin/knowledge/topics/tasks/${encodeURIComponent(taskId)}`, { method: 'GET' }, token)
  }
  async lintKnowledgeTopic(topic, token = null) {
    return this.makeAuthenticatedRequest('/admin/knowledge/topics/lint', {
      method: 'POST', body: JSON.stringify({ topic })
    }, token)
  }
  async seedKnowledgeTopic(payload, token = null) {
    return this.makeAuthenticatedRequest('/admin/knowledge/topics/seed', {
      method: 'POST', body: JSON.stringify(payload || {})
    }, token)
  }
  async markOnboardingCompleted(token = null) {
    return this.makeAuthenticatedRequest('/users/onboarding_completed', { method: 'PUT' }, token)
  }
  /**
   * Records one product event server-side. Mirrors the Plausible call — see
   * services/productEvents.js for why we keep our own copy. The backend
   * allowlists `name` and dedupes per (user, name, prop_value).
   */
  async recordAppEvent(name, propKey, propValue, token = null) {
    return this.makeAuthenticatedRequest('/events', {
      method: 'POST',
      body: JSON.stringify({ name, prop_key: propKey, prop_value: propValue })
    }, token, { silentErrorStatuses: [400, 401, 403] })
  }
  async getAdminOnboardingFunnel(days = 30, token = null) {
    return this.makeAuthenticatedRequest(`/admin/growth/onboarding-funnel?days=${encodeURIComponent(days)}`, { method: 'GET' }, token)
  }
  async updateUsername(username, token = null) {
    return this.makeAuthenticatedRequest('/users/username', {
      method: 'PUT',
      body: JSON.stringify({ username })
    }, token)
  }
  async updateLanguage(language, token = null) {
    return this.makeAuthenticatedRequest('/users/language', {
      method: 'PUT',
      body: JSON.stringify({ language })
    }, token)
  }
  async verifyAppStoreTransaction(transactionPayload, token = null) {
    return this.makeAuthenticatedRequest('/subscriptions/app-store/transactions', {
      method: 'POST',
      body: JSON.stringify(transactionPayload)
    }, token)
  }
  async redeemPromoCode(code, token = null) {
    const queryParams = new URLSearchParams({ code })
    const result = await this.makeAuthenticatedRequest(`/promo/redeem?${queryParams.toString()}`, {
      method: 'POST'
    }, token)
    // Campaign measurement: the code is a campaign id, never PII.
    import('./analytics.js').then(m => m.trackEvent('promo_redeem', { code })).catch(() => { })
    return result
  }
  async getLinks(params = {}, token = null, shareToken = null, { expectCollectionMissing = false } = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.offset) queryParams.append('offset', params.offset)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.collection_id) queryParams.append('collection_id', params.collection_id)
    if (params.saved_search_id) queryParams.append('saved_search_id', params.saved_search_id)
    if (params.tags && params.tags.length > 0) queryParams.append('tags', params.tags.join(','))
    if (params.queue || params.inbox) queryParams.append('in_inbox', 'true')
    if (params.query) queryParams.append('query', params.query)
    if (params.types && params.types.length > 0) queryParams.append('types', params.types.join(','))
    if (params.sort) queryParams.append('sort', params.sort)
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction)
    if (params.rating) queryParams.append('rating', params.rating)
    if (params.meta_filters) queryParams.append('meta_filters', params.meta_filters)

    const endpoint = `/links${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    
    // If shareToken is provided, add it as a custom header
    const options = { method: 'GET' }
    if (shareToken) {
      options.headers = { 'X-Share-Token': shareToken }
    }

    // A collection filter the server no longer resolves answers 404, and the
    // in-app library recovers from it by dropping the filter rather than
    // failing the whole read (LINKFLARE-16, utils/collectionFilterOutcome.js).
    // Reported, but flagged: an authenticated in-app rejection whose RATE we can
    // act on gets downgraded, never dropped (lessons.md).
    //
    // Declared by the caller and never inferred from the params: the public and
    // embed reads pass a share token instead of a user token, and their 404s are
    // anonymous public traffic with their own handling (#48 / LINKFLARE-Z).
    return this.makeAuthenticatedRequest(endpoint, options, token,
      expectCollectionMissing ? { expectedErrorStatuses: [404] } : {})
  }

  async getUpcomingReminders(token = null) {
    return this.makeAuthenticatedRequest('/links/reminders?days=365', { method: 'GET' }, token)
  }

  async getLink(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}`, { method: 'GET' }, token)
  }

  // `background: true` is for useLinkPolling, which looks for a timed-out save on
  // a backoff timer, swallows each attempt's failure and simply tries again; its
  // give-up path owns the outcome the user sees. One lost attempt is the same
  // invisible blip as a lost notifications poll, so it is filed flagged, not as
  // an exception (LINKFLARE-13 / 142796941).
  async getLinkByUrl(url, token = null, { background = false } = {}) {
    const queryParams = new URLSearchParams()
    queryParams.append('url', url)
    return this.makeAuthenticatedRequest(`/links?${queryParams.toString()}`, { method: 'GET' }, token, {
      expectedTransportFailure: background
    })
  }

  async searchLinks(query, params = {}, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('query', query)
    
    if (params.offset) queryParams.append('offset', params.offset)
    if (params.limit) queryParams.append('limit', params.limit)

    const endpoint = `/links?${queryParams.toString()}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token)
  }

  // `background: true` is for the offline queue REPLAYING a save that was already
  // parked (App.vue startup, the `online` listener, saveRecovery's delayed flush).
  // Nobody is waiting on that PUT: flush() removes an item only after it succeeds,
  // so a transport failure leaves it queued and the next trigger tries again — the
  // user sees nothing either way. Filing it as an exception meant a phone on a
  // flapping connection re-reported the same parked save indefinitely, each event
  // also uploading a session replay at replaysOnErrorSampleRate 1.0.
  //
  // The FOREGROUND save deliberately does NOT pass this: that is the attempt that
  // can cost the user their bookmark, and #46's recovery exists to catch it.
  // (Sentry linkflare LINKFLARE-11 / 142480706.) It may pass `deferLostReport`
  // instead when it polls for the link afterwards: the report then waits for the
  // poll's verdict and keeps full weight unless the server turns out to have the
  // link (see deferredLostReport).
  //
  // A pending card's temp id must never reach this body. The backend reads the
  // id leniently (`tryGetLong`), so "temp-…" becomes null and the update turns
  // into a CREATE by URL, answered 200. The card's parked save then replays over
  // it and silently reverts the edit. Every per-link method in this class puts
  // its id through `serverLinkId` for the same reason; for the path and query
  // params the symptom is a 404 instead (LINKFLARE-1B, LINKFLARE-1G). Writes to
  // a pending card go to its parked payload (`amendParkedSave`).
  async saveLink(linkData, token = null, runRules = true, { background = false, deferLostReport = false } = {}) {
    serverLinkId(linkData?.id)
    const endpoint = runRules ? '/links' : '/links?run_rules=false'
    return this.makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(linkData)
    }, token, { timeout: 60000, expectedTransportFailure: background, deferLostReport: deferLostReport && !background })
  }

  // Legacy methods for backward compatibility
  async addLink(linkData, token = null, options = {}) {
    return this.saveLink(linkData, token, true, options)
  }

  // Updating an existing link must NOT re-run automation rules by default: a manual edit
  // is authoritative, so a "type=VIDEO → add to inbox" rule must not override the user
  // explicitly toggling "In Inbox" off. Pass runRules=true to opt back in. (saveLink keeps
  // rules ON because it is the create/ingestion path.)
  async updateLink(linkData, token = null, runRules = false) {
    return this.saveLink(linkData, token, runRules)
  }

  // DELETE is idempotent: a 404 means the link is already gone, which is the
  // end state the caller asked for. Rethrowing it sent both delete paths in
  // Bookmarks.vue into their catch block — "couldn't delete this bookmark",
  // and the card left sitting in a list it no longer belonged to. Server
  // tracing showed why it fires at all: the same link deleted twice about a
  // second apart, first answered 200, then 404. A confirm button on a phone
  // does that. (LINKFLARE-14)
  //
  // Reported, not swallowed: `expectedErrorStatuses` files it at warning under
  // its own fingerprint so the double-fire RATE stays visible without paging.
  async deleteLink(linkId, token = null) {
    try {
      return await this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}`, {
        method: 'DELETE'
      }, token, { expectedErrorStatuses: [404] })
    } catch (error) {
      if (!isAlreadyDeleted(error)) throw error
      return { alreadyDeleted: true }
    }
  }

  async rateLink(linkId, rating, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('rating', rating)
    
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/rating?${queryParams.toString()}`, {
      method: 'PUT'
    }, token)
  }

  async recordLinkOpened(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/opened`, {
      method: 'POST'
    }, token)
  }

  async getReadableArticle(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/read`, {
      method: 'GET'
    }, token)
  }

  // Generate (or fetch the cached) Pro-only AI summary for an ARTICLE link.
  async summarizeArticle(linkId, token = null, { force = false } = {}) {
    const query = force ? '?force=true' : ''
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/summarize${query}`, {
      method: 'POST'
    }, token)
  }

  // Generate (or fetch the cached) Pro-only spoken audio ("Listen") for an ARTICLE link.
  // Returns { audio: { url, lang, model, chars, duration } } on success, or
  // { audio: { fallback: true, lang } } when the language isn't speakable by the engine.
  async ttsArticle(linkId, token = null, { force = false } = {}) {
    const query = force ? '?force=true' : ''
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/tts${query}`, {
      method: 'POST'
    }, token)
  }

  // Collections API methods
  async getCollections(token = null) {
    return this.makeAuthenticatedRequest('/collections', { method: 'GET' }, token)
  }

  async saveCollection(collectionData, token = null) {
    
    return this.makeAuthenticatedRequest('/collections', {
      method: 'PUT',
      body: JSON.stringify(collectionData)
    }, token)
  }

  async getPublicCollection(shareId, token = null) {
    const headers = {}
    if (token) {
      headers['X-Share-Token'] = token
    }
    
    // A share link that no longer resolves is a normal outcome for public
    // content (unshared, deleted, or a stale/mistyped link), and the viewer
    // already renders the not-found state. It still rejects for that UI.
    return this.makeAuthenticatedRequest(`/collections/${shareId}`, {
      method: 'GET',
      headers
    }, null, { silentErrorStatuses: [404] })
  }

  async verifyCollectionPassword(shareId, password) {
    return this.makeAuthenticatedRequest(`/collections/${shareId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ password })
    }, null)
  }

  // Legacy methods for backward compatibility
  async addCollection(collectionData, token = null) {
    return this.saveCollection(collectionData, token)
  }

  async updateCollection(collectionData, token = null) {
    return this.saveCollection(collectionData, token)
  }

  async deleteCollection(collectionId, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}`, {
      method: 'DELETE'
    }, token)
  }

  // Collection Collaboration API methods

  // A 400 here is the backend turning the invite down on purpose: the invitee is
  // already a collaborator, it is the owner's own address, or a free plan already
  // has its one collaborator. The dialog shows the rejection, so it is not a
  // fault, but how often free users hit the limit is worth seeing. Filed at
  // warning via `expectedErrorStatuses`, not dropped. (LINKFLARE-1H)
  async inviteCollaborator(collectionId, email, role, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, role })
    }, token, { expectedErrorStatuses: [400] })
  }

  async getCollaborators(collectionId, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}/collaborators`, {
      method: 'GET'
    }, token)
  }

  async updateCollaboratorRole(collectionId, userId, role, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}/collaborators/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role })
    }, token)
  }

  async removeCollaborator(collectionId, userId, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}/collaborators/${userId}`, {
      method: 'DELETE'
    }, token)
  }

  async leaveCollection(collectionId, token = null) {
    return this.makeAuthenticatedRequest(`/collections/${collectionId}/leave`, {
      method: 'DELETE'
    }, token)
  }

  async revokeInvitation(invitationId, token = null) {
    return this.makeAuthenticatedRequest(`/invitations/${invitationId}`, {
      method: 'DELETE'
    }, token)
  }

  async getMyInvitations(token = null) {
    return this.makeAuthenticatedRequest('/invitations', {
      method: 'GET'
    }, token)
  }

  async getInvitationByToken(invitationToken, token = null) {
    return this.makeAuthenticatedRequest(`/invitations/by-token/${invitationToken}`, {
      method: 'GET'
    }, token)
  }

  async acceptInvitation(invitationToken, token = null) {
    return this.makeAuthenticatedRequest(`/invitations/${invitationToken}/accept`, {
      method: 'POST'
    }, token)
  }

  async declineInvitation(invitationToken, token = null) {
    return this.makeAuthenticatedRequest(`/invitations/${invitationToken}/decline`, {
      method: 'POST'
    }, token)
  }

  async getSharedWithMeCollections(token = null) {
    return this.makeAuthenticatedRequest('/collections/shared', {
      method: 'GET'
    }, token)
  }

  // Saved Searches API methods
  async getSavedSearches(token = null) {
    return this.makeAuthenticatedRequest('/searches', { method: 'GET' }, token)
  }

  async saveSavedSearch(searchData, token = null) {
    
    return this.makeAuthenticatedRequest('/searches', {
      method: 'PUT',
      body: JSON.stringify(searchData)
    }, token)
  }

  async deleteSavedSearch(searchId, token = null) {
    return this.makeAuthenticatedRequest(`/searches/${searchId}`, {
      method: 'DELETE'
    }, token)
  }

  async getPublicSearch(shareId, token = null) {
    const headers = {}
    if (token) {
      headers['X-Share-Token'] = token
    }
    
    // Same as getPublicCollection: an unresolvable share link is expected.
    return this.makeAuthenticatedRequest(`/searches/${shareId}`, {
      method: 'GET',
      headers
    }, null, { silentErrorStatuses: [404] })
  }

  async verifySearchPassword(shareId, password) {
    return this.makeAuthenticatedRequest(`/searches/${shareId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ password })
    }, null)
  }

  // Tags API methods
  async getTags(token = null) {
    return this.makeAuthenticatedRequest('/tags', { method: 'GET' }, token)
  }

  async saveTag(tagData, token = null) {
    
    // If tag has an id, we're updating, so include it in the URL
    const endpoint = '/tags'
    
    return this.makeAuthenticatedRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(tagData)
    }, token)
  }

  async deleteTag(tagId, token = null) {
    return this.makeAuthenticatedRequest(`/tags/${tagId}`, {
      method: 'DELETE'
    }, token)
  }

  // Rules API methods
  async getRules(token = null) {
    return this.makeAuthenticatedRequest('/rules', { method: 'GET' }, token)
  }

  async saveRule(ruleData, token = null) {
    
    return this.makeAuthenticatedRequest('/rules', {
      method: 'PUT',
      body: JSON.stringify(ruleData)
    }, token)
  }

  async deleteRule(ruleId, token = null) {
    return this.makeAuthenticatedRequest(`/rules/${ruleId}`, {
      method: 'DELETE'
    }, token)
  }

  // User API methods
  async setPrivateModePassword(password, token = null) {
    
    // SECURITY FIX: Send password in request body instead of URL query parameter
    return this.makeAuthenticatedRequest('/users/private_mode_password', {
      method: 'PUT',
      body: JSON.stringify({ password })
    }, token)
  }

  async checkPrivatePassword(password, token = null) {
    // SECURITY FIX: Send password in request body instead of URL query parameter
    return this.makeAuthenticatedRequest('/users/check_password', {
      method: 'POST',
      body: JSON.stringify({ password })
    }, token)
  }

  // Feed API methods
  // Both feed calls send the URL trimmed. The dialogs validate the trimmed
  // value but used to send the raw one, so a pasted leading space reached the
  // backend as `%20https://...` and was rejected as "not a feed" (LINKFLARE-1F).
  async analyzeFeed(url, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('url', url.trim())
    
    const endpoint = `/feeds/info?${queryParams.toString()}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token, {
      expectedErrorStatuses: [400]
    })
  }

  async subscribeToFeed(url, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('url', url.trim())
    
    const endpoint = `/feeds?${queryParams.toString()}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'PUT' }, token)
  }

  async unsubscribeFromFeed(feedId, token = null) {
    return this.makeAuthenticatedRequest(`/feeds/${feedId}`, {
      method: 'DELETE'
    }, token)
  }

  async getFeedSubscriptions(token = null) {
    return this.makeAuthenticatedRequest('/feeds', { method: 'GET' }, token)
  }

  // Import methods

  async uploadNoteImage(file, token = null) {
    const formData = new FormData()
    formData.append('image', file)

    const response = await fetch(`${this.baseURL}/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Image upload failed: ${errorText}`)
    }
    return response.json()
  }

  async importFromRaindrop(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)

    return fetch(`${this.baseURL}/links/import/raindrop${importQuery(options)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    }).then(async response => {
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Import failed: ${errorText}`)
      }
      return response.json()
    })
  }

  async importFromChrome(file, token = null, options = {}) {
    // Endpoint assumed to be /links/import/chrome; adjust if backend differs
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${this.baseURL}/links/import/chrome${importQuery(options)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Import failed: ${errorText}`)
    }
    return response.json()
  }

  async importUrlList(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)

    return fetch(`${this.baseURL}/links/import/urls${importQuery(options)}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    }).then(async response => {
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Import failed: ${errorText}`)
      }
      return response.json()
    })
  }

  async importFromPocket(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`${this.baseURL}/links/import/pocket${importQuery(options)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Import failed: ${errorText}`)
    }
    return response.json()
  }

  async importFromInstapaper(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`${this.baseURL}/links/import/instapaper${importQuery(options)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Import failed: ${errorText}`)
    }
    return response.json()
  }

  async importFromGoodreads(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`${this.baseURL}/links/import/goodreads${importQuery(options)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Import failed: ${errorText}`)
    }
    return response.json()
  }

  async importFromYoutube(file, token = null, options = {}) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await fetch(`${this.baseURL}/links/import/youtube${importQuery(options)}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    })
    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Import failed: ${errorText}`)
    }
    return response.json()
  }

  // Starts an import queued with { preview: true }; starting it twice is harmless.
  async startImportTask(taskId, token = null) {
    const response = await fetch(`${this.baseURL}/tasks/${taskId}/start`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok) {
      const errorText = await response.text()
      // 404 = the server no longer holds this import (useImportStart drops the warning)
      throw Object.assign(new Error(`Import failed: ${errorText}`), { status: response.status })
    }
    return response.json()
  }

  // Drops an import queued with { preview: true } that was never started.
  async discardImportTask(taskId, token = null) {
    const response = await fetch(`${this.baseURL}/tasks/${taskId}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    })
    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to discard import: ${response.status}`)
    }
  }

  async getTaskProgress(taskId, token = null) {
    const response = await fetch(`${this.baseURL}/tasks/${taskId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 404) {
        return null // Task not found or completed
      }
      const errorText = await response.text()
      throw new Error(`Failed to get task progress: ${errorText}`)
    }
    return response.json()
  }

  // Export methods
  async exportData(token = null) {
    const url = `${this.baseURL}/export`
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': token ? `Bearer ${token}` : undefined
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      if (response.status === 401) {
        throw new AuthenticationError(`Authentication failed: ${errorText}`, 401)
      }
      throw new Error(`Export failed: ${errorText}`)
    }

    const blob = await response.blob()
    // Try to parse filename from Content-Disposition
    const disposition = response.headers.get('content-disposition') || ''
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition)
    const filename = match ? decodeURIComponent(match[1] || match[2]) : 'linkflare-export.zip'

    return { blob, filename }
  }

  // Feedback/Questionnaire methods
  async getQuestionnaire(token = null) {
    return this.makeAuthenticatedRequest('/questionnaire', { method: 'GET' }, token)
  }

  async submitFeedback(feedbackData, token = null) {
    
    return this.makeAuthenticatedRequest('/questionnaire/feedback', {
      method: 'POST',
      body: JSON.stringify(feedbackData)
    }, token)
  }

  // Recipe methods
  async addRecipe(formData, token = null) {
    return this.makeAuthenticatedRequest('/recipes', {
      method: 'POST',
      body: formData
    }, token, { expectedErrorStatuses: [400] })
  }

  // Meal Plan API methods
  async getMealPlans(token = null) {
    return this.makeAuthenticatedRequest('/meal_plans', { method: 'GET' }, token)
  }

  async getMealPlan(id, token = null) {
    return this.makeAuthenticatedRequest(`/meal_plans/${id}`, { method: 'GET' }, token)
  }

  // The week's grocery list, computed on the server from the plan and each
  // recipe's stored ingredients (nothing is persisted, so there is no save
  // counterpart). `from` / `to` are ISO dates, inclusive: the planner's week.
  async getMealPlanGroceryList(id, from, to, token = null) {
    const query = new URLSearchParams({ from, to })
    return this.makeAuthenticatedRequest(`/meal_plans/${id}/grocery_list?${query.toString()}`, { method: 'GET' }, token)
  }

  async saveMealPlan(mealPlan, token = null) {
    // If the plan has an ID, we might want to update that specific plan
    // But keeping existing behavior for "own" plan (usually id is null or handled by backend as 'current')
    // If we are editing a shared plan, we MUST use the specific endpoint if the backend requires it.
    // Assuming backend handles PUT /meal_plans for "my plan" and PUT /meal_plans/:id for specific.
    if (mealPlan.id) {
        return this.makeAuthenticatedRequest(`/meal_plans/${mealPlan.id}`, {
            method: 'PUT',
            body: JSON.stringify(mealPlan)
        }, token)
    }
    return this.makeAuthenticatedRequest('/meal_plans', {
      method: 'POST',
      body: JSON.stringify(mealPlan)
    }, token)
  }

  async shareMealPlan(id, email, token = null) {
    return this.makeAuthenticatedRequest(`/meal_plans/${id}/share`, {
      method: 'PUT',
      body: JSON.stringify({ email })
    }, token)
  }

  async unshareMealPlan(id, userId, token = null) {
    return this.makeAuthenticatedRequest(`/meal_plans/${id}/share/${userId}`, {
      method: 'DELETE'
    }, token)
  }

  // ========== Batch Operations ==========

  /**
   * Update multiple links in parallel (with concurrency limit).
   * Each item in `updates` should be a full link data object for saveLink.
   */
  async batchUpdateLinks(updates, token = null, concurrency = 5) {
    // Bulk edits operate on existing links, so route through updateLink (rules off) — otherwise a
    // bulk move/tag of video links would re-trigger a "VIDEO → inbox" rule and yank them back.
    return this._batchProcess(updates, (linkData) => this.updateLink(linkData, token), concurrency)
  }

  /**
   * Delete multiple links in parallel (with concurrency limit).
   */
  //
  // `succeeded` counts every call that RESOLVED, and an already-gone link now
  // resolves (LINKFLARE-14) — so it is the right number for "stop showing a
  // partial failure" and the wrong one for "how many rows went away". Callers
  // keeping their own count need the latter, so report it separately rather
  // than making them re-derive it.
  async batchDeleteLinks(linkIds, token = null, concurrency = 5) {
    let deleted = 0
    const result = await this._batchProcess(linkIds, async (id) => {
      const outcome = await this.deleteLink(id, token)
      // Single-threaded: every worker resumes on the same event loop, so this
      // read-modify-write cannot interleave.
      if (shouldCountDeletion(outcome)) deleted += 1
      return outcome
    }, concurrency)
    return { ...result, deleted }
  }

  /**
   * Internal helper: process items in batches with concurrency control.
   * Returns { succeeded: number, failed: Array<{ item, error }> }
   */
  async _batchProcess(items, fn, concurrency = 5) {
    const results = { succeeded: 0, failed: [] }
    const queue = [...items]

    const processNext = async () => {
      while (queue.length > 0) {
        const item = queue.shift()
        try {
          await fn(item)
          results.succeeded++
        } catch (error) {
          results.failed.push({ item, error })
        }
      }
    }

    const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => processNext())
    await Promise.all(workers)
    return results
  }

  // ─── Embed Analytics ──────────────────────────────────────────────────────
  async trackEmbedView(shareId, shareType, referrer) {
    try {
      await fetch(`${this.baseURL}/embed/${shareId}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ share_type: shareType, referrer })
      })
    } catch (e) {
      // Fire-and-forget, never throw
    }
  }

  async getEmbedAnalytics(shareId, token) {
    const response = await fetch(`${this.baseURL}/embed/${shareId}/analytics`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
    if (!response.ok) throw new Error('Failed to fetch embed analytics')
    return response.json()
  }

  // ─── oEmbed ───────────────────────────────────────────────────────────────
  async getOEmbed(shareId, shareType) {
    const response = await fetch(`${this.baseURL}/${shareType}s/${shareId}/oembed`)
    if (!response.ok) return null
    return response.json()
  }

  // ─── Public Page: Slug Resolution ─────────────────────────────────────────
  async resolveSlug(username, slug) {
    const params = new URLSearchParams({ username, slug })
    // An unknown username/slug pair is a normal 404 here — public page URLs are
    // guessable, editable and outlive the content they point at.
    return this.makeAuthenticatedRequest(`/public/resolve?${params.toString()}`, {
      method: 'GET'
    }, null, { silentErrorStatuses: [404] })
  }

  // ─── Public Page: View Tracking (fire-and-forget) ─────────────────────────
  async trackPublicView(shareId, shareType, referrer) {
    try {
      await fetch(`${this.baseURL}/public/${shareId}/view`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ share_type: shareType, referrer })
      })
    } catch (e) {
      // Fire-and-forget, never throw
    }
  }

  // ─── Public User Profile ──────────────────────────────────────────────────
  async getPublicProfile(username) {
    const params = new URLSearchParams({ username })
    return this.makeAuthenticatedRequest(`/public/profile?${params.toString()}`, {
      method: 'GET'
    }, null)
  }

  // ─── Public Share Discovery & Subscriptions ───────────────────────────────
  // Unified surface across collections, saved searches, and knowledge cards.
  // share_type query param disambiguates; share_id is the public share id.

  async discoverShares(params = {}, token = null) {
    const qp = new URLSearchParams()
    if (params.type) qp.append('type', params.type)
    if (params.q) qp.append('q', params.q)
    if (params.sort) qp.append('sort', params.sort)
    if (params.offset != null) qp.append('offset', params.offset)
    if (params.limit != null) qp.append('limit', params.limit)
    // Admin curation passes includeSelf:true so the admin can feature their
    // own discoverable shares. Backend silently ignores it for non-admins.
    if (params.includeSelf) qp.append('include_self', 'true')
    const endpoint = `/discover${qp.toString() ? '?' + qp.toString() : ''}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token)
  }

  async previewSubscription(shareId, shareType, token = null) {
    const qp = new URLSearchParams({ type: shareType })
    return this.makeAuthenticatedRequest(`/shares/${shareId}/preview?${qp.toString()}`, {
      method: 'GET'
    }, token)
  }

  async subscribeShare(shareId, shareType, options = {}, token = null) {
    const qp = new URLSearchParams({ type: shareType })
    const body = {}
    if (options.parent_id != null) body.parent_id = options.parent_id
    const result = await this.makeAuthenticatedRequest(`/shares/${shareId}/subscribe?${qp.toString()}`, {
      method: 'POST',
      body: JSON.stringify(body)
    }, token)
    // Instrumented here rather than at the five call sites (onboarding,
    // DiscoveryModal, DiscoverView, PublicView, PublicKnowledgeView) so no
    // subscribe path can be added later without being counted. Only the share
    // TYPE is sent — never the share id, which identifies content.
    import('./analytics.js').then(m => m.trackEvent('subscribe_share', { type: shareType })).catch(() => { })
    return result
  }

  async unsubscribeShare(shareId, token = null) {
    return this.makeAuthenticatedRequest(`/shares/${shareId}/subscribe`, {
      method: 'DELETE'
    }, token)
  }

  async syncSubscription(shareId, shareType, token = null) {
    const qp = new URLSearchParams({ type: shareType })
    return this.makeAuthenticatedRequest(`/shares/${shareId}/sync?${qp.toString()}`, {
      method: 'POST'
    }, token)
  }

  async getSubscriptions(token = null) {
    return this.makeAuthenticatedRequest('/subscriptions', { method: 'GET' }, token)
  }

  async setShareFeatured(shareId, shareType, featured, blurb = '', token = null) {
    const qp = new URLSearchParams({ type: shareType })
    return this.makeAuthenticatedRequest(`/shares/${shareId}/featured?${qp.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({ featured: !!featured, blurb })
    }, token)
  }

  async setShareDiscoverable(shareId, shareType, discoverable, token = null) {
    const qp = new URLSearchParams({ type: shareType })
    return this.makeAuthenticatedRequest(`/shares/${shareId}/discoverable?${qp.toString()}`, {
      method: 'PUT',
      body: JSON.stringify({ discoverable: !!discoverable })
    }, token)
  }

  async reportShare(shareId, shareType, { reason = '', notes = '' } = {}, token = null) {
    const qp = new URLSearchParams({ type: shareType })
    return this.makeAuthenticatedRequest(`/shares/${shareId}/report?${qp.toString()}`, {
      method: 'POST',
      body: JSON.stringify({ reason, notes })
    }, token)
  }

  // ─── Price Alerts ─────────────────────────────────────────────────────────
  async getPriceAlerts(token = null) {
    return this.makeAuthenticatedRequest('/price-alerts', { method: 'GET' }, token)
  }

  async getPriceAlertForLink(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/price-alerts/link/${serverLinkId(linkId)}`, { method: 'GET' }, token)
  }

  async savePriceAlert(alertData, token = null) {
    serverLinkId(alertData?.link_id)
    return this.makeAuthenticatedRequest('/price-alerts', {
      method: 'PUT',
      body: JSON.stringify(alertData)
    }, token)
  }

  async deletePriceAlert(alertId, token = null) {
    return this.makeAuthenticatedRequest(`/price-alerts/${alertId}`, {
      method: 'DELETE'
    }, token)
  }

  async deletePriceAlertForLink(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/price-alerts/link/${serverLinkId(linkId)}`, {
      method: 'DELETE'
    }, token)
  }

  async getPriceHistory(linkId, token = null) {
    return this.makeAuthenticatedRequest(`/price-alerts/link/${serverLinkId(linkId)}/history`, { method: 'GET' }, token)
  }

  // ─── Notification Preferences ─────────────────────────────────────────────
  async getNotificationPreferences(token = null) {
    return this.makeAuthenticatedRequest('/notification-preferences', { method: 'GET' }, token)
  }

  async updateNotificationPreferences(preferences, token = null) {
    return this.makeAuthenticatedRequest('/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences)
    }, token)
  }

  // ─── Streaming Availability (TMDB/JustWatch) ──────────────────────────────
  async getStreamingAvailability(linkId, token = null, { force = false } = {}) {
    return this.makeAuthenticatedRequest(`/links/${serverLinkId(linkId)}/streaming${force ? '?force=true' : ''}`, {
      method: 'POST'
    }, token)
  }

  async getStreamingProviders(region, token = null) {
    return this.makeAuthenticatedRequest(`/streaming/providers?region=${encodeURIComponent(region)}`, { method: 'GET' }, token)
  }

  async getStreamingRegions(token = null) {
    return this.makeAuthenticatedRequest('/streaming/regions', { method: 'GET' }, token)
  }

  async getStreamingPreferences(token = null) {
    return this.makeAuthenticatedRequest('/streaming-preferences', { method: 'GET' }, token)
  }

  async updateStreamingPreferences(preferences, token = null) {
    return this.makeAuthenticatedRequest('/streaming-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences)
    }, token)
  }

  // ─── Notification Log ─────────────────────────────────────────────────────
  // `background: true` is for the 60s activity poll in App.vue, which swallows
  // its own failure and re-fires on the next tick — a blip there is invisible to
  // the user, so it is filed as a flagged warning rather than an exception. The
  // notifications PAGE calls this too and deliberately leaves it off: a failure
  // the user is actually looking at stays a real error.
  async getNotifications(params = {}, token = null, { background = false } = {}) {
    const queryParams = new URLSearchParams()
    if (params.offset) queryParams.append('offset', params.offset)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.type) queryParams.append('type', params.type)
    const endpoint = `/notifications${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token, {
      expectedTransportFailure: background
    })
  }

  // ─── Knowledge Cards ──────────────────────────────────────────────────────
  async getKnowledgeCards(parentId = 0, params = {}, token = null) {
    const queryParams = new URLSearchParams()
    if (params.offset) queryParams.append('offset', params.offset)
    if (params.limit) queryParams.append('limit', params.limit)
    const endpoint = `/knowledge/${parentId}/children${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token)
  }

  async getKnowledgeCard(cardId, token = null) {
    return this.makeAuthenticatedRequest(`/knowledge/${cardId}`, { method: 'GET' }, token)
  }

  async searchKnowledge(query, params = {}, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('query', query)
    if (params.offset) queryParams.append('offset', params.offset)
    if (params.limit) queryParams.append('limit', params.limit)
    const endpoint = `/knowledge?${queryParams.toString()}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'GET' }, token)
  }

  async saveKnowledgeCard(cardData, token = null) {
    return this.makeAuthenticatedRequest('/knowledge', {
      method: 'PUT',
      body: JSON.stringify(buildKnowledgeCardPayload(cardData))
    }, token)
  }

  async deleteKnowledgeCard(cardId, token = null) {
    return this.makeAuthenticatedRequest(`/knowledge/${cardId}`, {
      method: 'DELETE'
    }, token)
  }

  async updateKnowledgeCardChildrenColor(cardId, color, token = null) {
    const queryParams = new URLSearchParams()
    queryParams.append('color', color)
    const endpoint = `/knowledge/${cardId}/children/color?${queryParams.toString()}`
    return this.makeAuthenticatedRequest(endpoint, { method: 'PUT' }, token)
  }

  // ---- Public Knowledge ----
  async getPublicKnowledgeCard(shareId) {
    return this.makeAuthenticatedRequest(`/knowledge/public/${shareId}`, { method: 'GET' }, null)
  }

  async getPublicKnowledgeChildren(shareId, parentId = null) {
    const params = new URLSearchParams()
    if (parentId) params.append('parent_id', parentId)
    const qs = params.toString()
    return this.makeAuthenticatedRequest(`/knowledge/public/${shareId}/children${qs ? '?' + qs : ''}`, { method: 'GET' }, null)
  }

  // ─── Account Deletion ──────────────────────────────────────────────────────
  async getDeletionStats(token = null) {
    return this.makeAuthenticatedRequest('/users/deletion-stats', { method: 'GET' }, token)
  }

  async deleteAccount(token = null) {
    return this.makeAuthenticatedRequest('/users', { method: 'DELETE' }, token)
  }

  // ─── Spaced Repetition Review ──────────────────────────────────────────────
  async getReviewQueue(type = 'all', limit = 20, token = null) {
    const params = new URLSearchParams({ type, limit })
    return this.makeAuthenticatedRequest(`/review/queue?${params.toString()}`, { method: 'GET' }, token)
  }

  async submitReview(type, id, rating, context = {}, token = null) {
    return this.makeAuthenticatedRequest(`/review/${type}/${id}`, {
      method: 'POST',
      body: JSON.stringify({ rating, ...context })
    }, token)
  }

  async getReviewStats(token = null) {
    return this.makeAuthenticatedRequest('/review/stats', { method: 'GET' }, token)
  }

  async getReviewHistory(offset = 0, limit = 50, token = null) {
    const params = new URLSearchParams({ offset, limit })
    return this.makeAuthenticatedRequest(`/review/history?${params.toString()}`, { method: 'GET' }, token)
  }

  async getCardReviewHistory(cardId, offset = 0, limit = 50, token = null) {
    const params = new URLSearchParams({ offset, limit })
    return this.makeAuthenticatedRequest(`/review/knowledge/${cardId}/history?${params.toString()}`, { method: 'GET' }, token)
  }

  async getMemoryStats(token = null) {
    return this.makeAuthenticatedRequest('/review/memory-stats', { method: 'GET' }, token)
  }

  async generateReviewQuestion(cardId, token = null) {
    return this.makeAuthenticatedRequest(`/review/knowledge/${cardId}/generate-question`, { method: 'POST' }, token)
  }

  async evaluateReviewAnswer(cardId, question, userAnswer, correctAnswer, token = null) {
    return this.makeAuthenticatedRequest(`/review/knowledge/${cardId}/evaluate`, {
      method: 'POST',
      body: JSON.stringify({ question, user_answer: userAnswer, correct_answer: correctAnswer })
    }, token)
  }

  // Media search & generation for knowledge cards
  async searchMediaImages(query, optionsOrNumber = 20, token = null) {
    const options = optionsOrNumber && typeof optionsOrNumber === 'object'
      ? optionsOrNumber
      : { number: optionsOrNumber }
    const params = new URLSearchParams({ query })
    if (options.number != null) params.set('number', String(options.number))
    if (options.offset != null) params.set('offset', String(options.offset))
    if (options.pageToken) params.set('pageToken', options.pageToken)
    return this.makeAuthenticatedRequest(`/media/images?${params.toString()}`, { method: 'GET' }, token)
  }

  async searchMediaGifs(query, optionsOrToken = null, token = null) {
    const options = optionsOrToken && typeof optionsOrToken === 'object' ? optionsOrToken : {}
    const authToken = optionsOrToken && typeof optionsOrToken === 'object' ? token : optionsOrToken
    const params = new URLSearchParams({ query })
    if (options.number != null) params.set('number', String(options.number))
    if (options.offset != null) params.set('offset', String(options.offset))
    if (options.pageToken) params.set('pageToken', options.pageToken)
    return this.makeAuthenticatedRequest(`/media/gifs?${params.toString()}`, { method: 'GET' }, authToken)
  }

  async searchMediaVideos({ query = '', url = '', number = null, offset = null, pageToken = '' } = {}, token = null) {
    const params = new URLSearchParams()
    if (query) params.set('query', query)
    if (url) params.set('url', url)
    if (number != null) params.set('number', String(number))
    if (offset != null) params.set('offset', String(offset))
    if (pageToken) params.set('pageToken', pageToken)
    return this.makeAuthenticatedRequest(`/media/videos?${params.toString()}`, { method: 'GET' }, token)
  }

  async generateMediaImage({ prompt = '', context = '' } = {}, token = null) {
    return this.makeAuthenticatedRequest('/media/generate-image', {
      method: 'POST',
      body: JSON.stringify({ prompt, context })
    }, token, { timeout: 120000 })
  }

  // ─── Highlights ────────────────────────────────────────────────────────────
  async getHighlights(params = {}, token = null) {
    const qp = new URLSearchParams()
    if (params.url) qp.append('url', params.url)
    if (params.link_id) qp.append('link_id', serverLinkId(params.link_id))
    if (params.offset != null) qp.append('offset', params.offset)
    if (params.limit != null) qp.append('limit', params.limit)
    const qs = qp.toString()
    return this.makeAuthenticatedRequest(`/highlights${qs ? '?' + qs : ''}`, { method: 'GET' }, token)
  }

  async getHighlightsForUrl(url, token = null) {
    return this.getHighlights({ url }, token)
  }

  async getHighlightsForLink(linkId, token = null) {
    return this.getHighlights({ link_id: linkId }, token)
  }

  async getHighlightCounts(token = null) {
    return this.makeAuthenticatedRequest('/highlights/counts', { method: 'GET' }, token)
  }

  async searchHighlights(query, params = {}, token = null) {
    const qp = new URLSearchParams()
    qp.append('q', query)
    if (params.offset != null) qp.append('offset', params.offset)
    if (params.limit != null) qp.append('limit', params.limit)
    return this.makeAuthenticatedRequest(`/highlights/search?${qp.toString()}`, { method: 'GET' }, token)
  }

  async saveHighlight(highlightData, token = null) {
    serverLinkId(highlightData?.link_id)
    return this.makeAuthenticatedRequest('/highlights', {
      method: 'PUT',
      body: JSON.stringify(highlightData)
    }, token)
  }

  async deleteHighlight(highlightId, token = null) {
    return this.makeAuthenticatedRequest(`/highlights/${highlightId}`, {
      method: 'DELETE'
    }, token)
  }
}

export { AuthenticationError, TimeoutError }
export default new ApiService()
